import { RendererProps, getStringPath, noop, resolveDynamicString } from 'components/a2ui/types'
import { RenderField } from 'components/core/dynamic-form/fields/RenderField'
import { getByPointer } from 'lib/a2ui/resolver'
import { Field, SelectField_SelectType } from 'pbts/c1/api/form/v1/form'
import { FC, useCallback, useMemo } from 'react'

type ChoicePickerRendererProps = Pick<RendererProps, 'component' | 'dataModel' | 'onDataChange' | 'disabled'>

/**
 * Renders a choice picker using C1's RenderField component with selectField view.
 * Maps A2UI choices to SelectOption array for consistent styling with other forms.
 */
export const ChoicePickerRenderer: FC<ChoicePickerRendererProps> = ({
    component,
    dataModel,
    onDataChange,
    disabled = false,
}) => {
    const cp = component.component.oneofKind === 'choicePicker' ? component.component.choicePicker : undefined
    const label = useMemo(
        () => (cp ? resolveDynamicString(cp.label, dataModel, getByPointer) : ''),
        [cp, dataModel],
    )
    const path = cp ? getStringPath(cp.value) : undefined
    const selectedValue = path ? String(getByPointer(dataModel, path) ?? '') : ''

    const options = useMemo(
        () =>
            cp?.choices.map((choice) => ({
                value: choice.id,
                displayName: resolveDynamicString(choice.label, dataModel, getByPointer),
                description: resolveDynamicString(choice.description, dataModel, getByPointer),
            })) ?? [],
        [cp?.choices, dataModel],
    )

    const field: Field = useMemo(() => ({
        name: component.id,
        displayName: label,
        description: '',
        required: false,
        type: {
            oneofKind: 'stringField',
            stringField: {
                defaultValue: '',
                placeholder: '',
                view: {
                    oneofKind: 'selectField',
                    selectField: {
                        type: SelectField_SelectType.BUTTONS,
                        options,
                    },
                },
            },
        },
        providerConfig: { oneofKind: undefined },
    }), [component.id, label, options])

    const handleChange = useCallback((value: string): void => {
        if (path) onDataChange(path, value)
    }, [path, onDataChange])

    const cField = useMemo(() => ({
        value: selectedValue,
        onChange: handleChange,
        onBlur: noop,
        name: component.id,
        ref: noop,
    }), [selectedValue, handleChange, component.id])

    if (!cp) return null

    return <RenderField field={field} cField={cField} errors={{}} disabled={disabled} />
}
